import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { NumberParam, StringParam, useQueryParams } from "use-query-params";
import useNotification from "../../hooks/useNotification";
import useTranslations from "../../hooks/useTranslations";
import useViewer from "../../hooks/useViewer";
import {
  EditIcon,
  PlusIcon,
  SortAscendingDirectionIcon,
  SortDescendingDirectionIcon,
} from "../icons";
import List from "../utils/List";
import Pagination from "../utils/Pagination";
import SearchInput from "../utils/SearchInput";
import SearchPopover from "../utils/SearchPopover";
import SearchPopoverItem from "../utils/SearchPopoverItem";
import SearchPopoverHeading from "../utils/SearchPopoverHeading";
import { Button, ButtonGroup, H6, Link } from "../wombat-ui";
import { orderBy } from "lodash";
import EditSkillDialog from "./EditSkillDialog";
import { useToggle, useDebounce } from "react-use";
import ProfessionTag from "../professions/ProfessionTag";
import { TagCloseButton } from "@chakra-ui/react";
import {
  ProfessionFragment,
  SkillFragment,
  useDeleteProfessionSkill,
  useGetProfessionsLazyQuery,
  useGetSkillsWithProfessions,
  useInsertProfessionSkill,
} from "../../graphql/generated/definitions";

const ProfessionSearchItem: React.FC<{
  profession: ProfessionFragment;
  baseId?: string;
}> = ({ profession, baseId }) => (
  <SearchPopoverItem id={`${baseId}${profession.id}`}>
    <div className="flex space-x-2">
      <p>{profession.name}</p>
    </div>
  </SearchPopoverItem>
);

const SkillItem = ({ skill, setSkillDialog, refetchSkills }) => {
  const t = useTranslations();
  const { notifySuccess, notifyError } = useNotification();
  const [insertProfessionSkill] = useInsertProfessionSkill();
  const [deleteProfessionSkill] = useDeleteProfessionSkill();
  const { companyId } = useViewer();

  const [getProfessions, { data }] = useGetProfessionsLazyQuery({
    variables: {
      companyId,
      sortBy: "name",
    },
    fetchPolicy: "cache-and-network",
  });
  const professions = data?.professions?.collection || [];

  const ordereSkillProfessions = useMemo(
    () => orderBy(skill.professions, ({ name }) => name.toLocaleLowerCase()),
    [skill.professions]
  );

  const handleItemSelected = useCallback(
    async (profession: ProfessionFragment) => {
      try {
        await insertProfessionSkill({
          variables: {
            input: {
              skillId: skill.id,
              professionId: profession.id,
            },
          },
        });

        notifySuccess({
          title: t(
            `professionSkillLibrary.notification.professionSkill.change.success`
          ) as string,
        });

        refetchSkills();
      } catch {
        notifyError({
          title: t(
            `professionSkillLibrary.notification.professionSkill.change.error`
          ) as string,
        });
      }
    },
    [refetchSkills, deleteProfessionSkill, insertProfessionSkill]
  );

  const [search, setSearch] = useState("");

  // no control, it's just a flag to avoid making useless
  // requests when the popover is not open
  const [popoverOpen, togglePopoverOpen] = useToggle(false);

  useDebounce(
    () => {
      if (popoverOpen) {
        getProfessions({ variables: { q: search } });
      }
    },
    200,
    [search, popoverOpen, getProfessions]
  );

  // clear search on close
  useEffect(() => {
    if (!popoverOpen) {
      setSearch("");
      getProfessions({ variables: { q: "" } });
    }
  }, [popoverOpen, getProfessions]);

  const filteredProfessions = useMemo(
    () =>
      professions.filter(
        ({ id: professionId }) =>
          !skill.professions.find(
            ({ id: professionSkillId }) => professionId === professionSkillId
          )
      ),
    [professions, skill]
  );

  const handleRemoveProfession = useCallback(
    async (profession) => {
      try {
        await deleteProfessionSkill({
          variables: {
            input: {
              skillId: skill.id,
              professionId: profession.id,
            },
          },
        });

        notifySuccess({
          title: t(
            `professionSkillLibrary.notification.professionSkill.change.success`
          ) as string,
        });

        refetchSkills();
      } catch {
        notifyError({
          title: t(
            `professionSkillLibrary.notification.professionSkill.change.error`
          ) as string,
        });
      }
    },
    [deleteProfessionSkill, refetchSkills, skill.id]
  );

  const havingAnswersMatching = useMemo(
    () => JSON.stringify([{ skillId: skill.id }]),
    [skill.id]
  );

  return (
    <div
      key={skill.id}
      id={`skill_library_${skill.id}`}
      className="grid items-center grid-cols-12 py-4 pt-2"
    >
      <div className="flex flex-row items-center col-span-3 gap-2 group">
        <Link
          to={`/users?havingAnswersMatching=${encodeURIComponent(
            havingAnswersMatching
          )}`}
        >
          <p className="text-sm">{skill.name}</p>
        </Link>
        <Button
          icon={EditIcon}
          onClick={() => setSkillDialog({ open: true, skill })}
          id={`openEditSkillDialog_${skill.id}`}
          variant="ghost"
          scale="xs"
          className="opacity-0 group-hover:opacity-100"
        />
      </div>
      <div className="col-span-9">
        <div className="flex items-center justify-end pt-2">
          <div className="flex flex-wrap justify-end gap-2 pr-2">
            {ordereSkillProfessions.map((profession: ProfessionFragment) => (
              <ProfessionTag profession={profession} key={profession.id}>
                <TagCloseButton
                  data-test-id={`remove_badge_profession_badge_${profession.id}`}
                  onClick={() => handleRemoveProfession(profession)}
                />
              </ProfessionTag>
            ))}
          </div>
          {skill.canInsert && (
            <SearchPopover
              title={
                <SearchPopoverHeading>
                  {t("professionSkillLibrary.professions")}
                </SearchPopoverHeading>
              }
              loading={false}
              placeholder={
                t("professionSkillLibrary.filterProfessions") as string
              }
              items={filteredProfessions}
              align="bottom-right"
              onItemSelected={handleItemSelected}
              onSearchChange={setSearch}
              searchValue={search}
              button={
                <Button
                  data-test-id={`${skill.id}_add_profession`}
                  variant="ghost"
                  icon={PlusIcon}
                  onClick={togglePopoverOpen}
                />
              }
              noMatch={
                <SearchPopoverItem>
                  {t("wombat.select.emptyResult")}
                </SearchPopoverItem>
              }
              renderItem={(item: ProfessionFragment) => (
                <ProfessionSearchItem profession={item} baseId={skill.id} />
              )}
            />
          )}
        </div>
      </div>
    </div>
  );
};

interface SkillLibraryProps {
  locale: string;
}

const SkillLibrary: React.FC<SkillLibraryProps> = ({
  locale,
}) => {
  const t = useTranslations();
  const { isCapableOfInsertingSkills } = useViewer();
  const [skillDialog, setSkillDialog] = useState({
    open: false,
    skill: null,
  });

  const [{ page, skillQ, direction }, setQuery] = useQueryParams({
    page: NumberParam,
    sortBy: StringParam,
    direction: StringParam,
    skillQ: StringParam,
  });

  const [search, setSearch] = useState(skillQ);
  useEffect(() => setSearch(skillQ), [skillQ]);

  const {
    data,
    loading,
    refetch: refetchSkills,
  } = useGetSkillsWithProfessions({
    variables: {
      q: skillQ || undefined,
      page: page || 1,
      per: 20,
      sortBy: "name",
      direction,
      locale
    },
  });

  const skills = data?.skills?.collection || [];
  const itemsCount = data?.skills?.metadata?.totalCount || 0;

  const handleCloseSkillDialog = useCallback(() => {
    setSkillDialog({ open: false, skill: null });
  }, [setSkillDialog, refetchSkills]);

  const handleChangeSkillDialog = useCallback(
    (result) => {
      refetchSkills();
      handleCloseSkillDialog();

      // auto-set the search on create
      if (!skillDialog?.skill) {
        setQuery({ skillQ: result.name });
      }
    },
    [handleCloseSkillDialog, refetchSkills, skillDialog?.skill]
  );

  const handleDeleteSkillDialog = useCallback(() => {
    refetchSkills();
    handleCloseSkillDialog();
  }, [handleCloseSkillDialog, refetchSkills]);

  return (
    <>
      <div className="flex flex-col space-y-4">
        <>
          <List
            isLoading={loading}
            emptyMessage={t("professionSkillLibrary.noMatchingSkills") as string}
            data={skills}
            listOnly
            renderAsList={(data: SkillFragment) => (
              <SkillItem
                skill={data}
                key={data.id}
                setSkillDialog={setSkillDialog}
                refetchSkills={refetchSkills}
              />
            )}
            id="skills-list"
          >
            <SearchInput
              onSubmit={(q) => setQuery({ page: 1, skillQ: q })}
              label={t("list.search") as string}
              placeholder={
                t("professionSkillLibrary.searchPlaceholder") as string
              }
              value={search}
              onChange={setSearch}
            />
            <div className="flex space-x-3">
              {isCapableOfInsertingSkills && (
                <Button
                  onClick={() => setSkillDialog({ open: true, skill: null })}
                  id="createSkillButton"
                >
                  {t("professionSkillLibrary.dialog.skill.title.create")}
                </Button>
              )}
              <ButtonGroup scale="sm">
                <Button
                  icon={SortAscendingDirectionIcon}
                  onClick={() => setQuery({ direction: "ASC" })}
                  variant={
                    direction === "ASC" || !direction ? "primary" : undefined
                  }
                />
                <Button
                  icon={SortDescendingDirectionIcon}
                  onClick={() => setQuery({ direction: "DESC" })}
                  variant={direction === "DESC" ? "primary" : undefined}
                />
              </ButtonGroup>
            </div>
          </List>
          {!loading && (
            <Pagination
              page={page}
              itemsCount={itemsCount}
              onChange={(page) => setQuery({ page })}
              itemsPerPage={20}
            />
          )}
        </>
      </div>
      <EditSkillDialog
        open={skillDialog.open}
        onClose={handleCloseSkillDialog}
        onInsert={handleChangeSkillDialog}
        onDelete={handleDeleteSkillDialog}
        skill={skillDialog.skill}
      />
    </>
  );
};

export default memo(SkillLibrary);
